"use client";

import { useState, useCallback, useEffect } from "react";
import {
  generateImage,
  uploadImage,
  getImageList,
  GenerateRequest,
  GenerateResponse,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface GenerationState {
  isGenerating: boolean;
  isUploading: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  currentImage: GenerateResponse | null;
  history: GenerateResponse[];
}

export function useImageGeneration() {
  const { isAuthenticated, user } = useAuth();

  const [state, setState] = useState<GenerationState>({
    isGenerating: false,
    isUploading: false,
    isLoadingHistory: false,
    error: null,
    currentImage: null,
    history: [],
  });

  // Load history when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      loadHistory();
    } else {
      // Clear history when logged out
      setState((prev) => ({
        ...prev,
        history: [],
        currentImage: null,
      }));
    }
  }, [isAuthenticated, user]);

  const loadHistory = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoadingHistory: true }));

    try {
      const response = await getImageList(50, 0);
      setState((prev) => ({
        ...prev,
        isLoadingHistory: false,
        history: response.images,
        // Set the most recent image as current if none selected
        currentImage: prev.currentImage || response.images[0] || null,
      }));
    } catch (error) {
      console.error("Failed to load history:", error);
      setState((prev) => ({
        ...prev,
        isLoadingHistory: false,
      }));
    }
  }, []);

  const generate = useCallback(async (request: GenerateRequest) => {
    setState((prev) => ({
      ...prev,
      isGenerating: true,
      error: null,
    }));

    try {
      const result = await generateImage(request);

      setState((prev) => ({
        ...prev,
        isGenerating: false,
        currentImage: result,
        history: [result, ...prev.history].slice(0, 50), // Keep last 50
      }));

      return result;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to generate image";

      setState((prev) => ({
        ...prev,
        isGenerating: false,
        error: message,
      }));

      throw error;
    }
  }, []);

  const upload = useCallback(async (file: File) => {
    setState((prev) => ({
      ...prev,
      isUploading: true,
      error: null,
    }));

    try {
      const result = await uploadImage(file);

      setState((prev) => ({
        ...prev,
        isUploading: false,
        currentImage: result,
        history: [result, ...prev.history].slice(0, 50), // Keep last 50
      }));

      return result;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to upload image";

      setState((prev) => ({
        ...prev,
        isUploading: false,
        error: message,
      }));

      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const selectFromHistory = useCallback((image: GenerateResponse) => {
    setState((prev) => ({ ...prev, currentImage: image }));
  }, []);

  const removeFromHistory = useCallback((imageId: string) => {
    setState((prev) => {
      const newHistory = prev.history.filter((img) => img.image_id !== imageId);
      const newCurrentImage =
        prev.currentImage?.image_id === imageId
          ? newHistory[0] || null
          : prev.currentImage;
      return {
        ...prev,
        history: newHistory,
        currentImage: newCurrentImage,
      };
    });
  }, []);

  const refreshHistory = useCallback(() => {
    if (isAuthenticated) {
      loadHistory();
    }
  }, [isAuthenticated, loadHistory]);

  return {
    ...state,
    generate,
    upload,
    clearError,
    selectFromHistory,
    removeFromHistory,
    refreshHistory,
  };
}
